import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { Artefato } from "../../components/Artefato.js";
import { Botao, Campo, Entrada, Falha } from "../../components/ui.js";
import { useAuth } from "./useAuth.js";

/**
 * Recuperar a senha.
 *
 * Dois passos: pedir o código e trocar a senha. A tela **não diz** se o e-mail
 * tem conta — a mesma mensagem aparece nos dois casos, senão qualquer pessoa
 * descobriria quem é cliente aqui digitando endereços.
 */
export function RecuperarSenhaPage() {
  const { autenticado, carregando } = useAuth();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [pedido, setPedido] = useState(false);

  const pedir = trpc.auth.pedirRedefinicao.useMutation({
    onSuccess: () => {
      setErro(null);
      setPedido(true);
    },
    onError: (e) => setErro(e.message),
  });

  const redefinir = trpc.auth.redefinirSenha.useMutation({
    onSuccess: async () => {
      setErro(null);
      await queryClient.invalidateQueries();
    },
    onError: (e) => setErro(e.message),
  });

  if (!carregando && autenticado) return <Navigate to="/nova" replace />;

  return (
    <main className="flex min-h-full flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        <div className="flex items-center gap-3.5">
          <Artefato tamanho="tira" estado="vazio" rotulos={false} className="h-14 [&>div]:h-14" />
          <div>
            <h1 className="font-display text-[1.75rem] leading-none tracking-[-0.02em]">
              Recuperar senha
            </h1>
            <p className="mt-1.5 text-[0.9375rem] text-texto-suave">
              {pedido ? "Digite o código e a nova senha." : "Informe o e-mail da sua conta."}
            </p>
          </div>
        </div>

        {!pedido ? (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setErro(null);
              pedir.mutate({ email: email.trim().toLowerCase() });
            }}
            className="mt-8 flex flex-col gap-4"
          >
            <Campo
              rotulo="E-mail da conta"
              dica="O código vai para o WhatsApp cadastrado, ou para este e-mail."
            >
              <Entrada
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                required
                disabled={pedir.isPending}
              />
            </Campo>

            {erro && <Falha mensagem={erro} />}

            <Botao type="submit" tom="brasa" largo carregando={pedir.isPending} className="mt-2">
              Enviar código
            </Botao>
          </form>
        ) : (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setErro(null);
              redefinir.mutate({ codigo: codigo.trim(), novaSenha: senha });
            }}
            className="mt-8 flex flex-col gap-4"
          >
            {/* Sem confirmar se a conta existe: a mensagem é a mesma para todo
                e-mail digitado, e é isso que impede a tela de virar um
                verificador de quem é cliente aqui. */}
            <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
              Se houver uma conta com esse e-mail, o código já foi enviado.
            </p>

            <Campo rotulo="Código">
              <Entrada
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                required
                disabled={redefinir.isPending}
                className="text-center text-[1.5rem] tracking-[0.3em]"
              />
            </Campo>

            <Campo rotulo="Nova senha" dica="Ao menos 8 caracteres.">
              <Entrada
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="new-password"
                required
                disabled={redefinir.isPending}
              />
            </Campo>

            {erro && <Falha mensagem={erro} />}

            <Botao
              type="submit"
              tom="brasa"
              largo
              carregando={redefinir.isPending}
              disabled={codigo.length !== 6 || senha.length < 8}
              className="mt-2"
            >
              Trocar a senha
            </Botao>

            <p className="text-center text-[0.8125rem] leading-relaxed text-texto-fraco">
              Trocar a senha encerra as sessões abertas nos outros aparelhos.
            </p>
          </form>
        )}

        <p className="mt-8 text-center text-[0.875rem] text-texto-suave">
          Lembrou a senha?{" "}
          <Link to="/entrar" className="text-brasa underline underline-offset-4">
            Entrar
          </Link>
        </p>
      </div>
    </main>
  );
}
